import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  addWatchlistMember,
  createWatchlist,
  deleteWatchlist,
  getWatchlist,
  listSecurities,
  listWatchlists,
  removeWatchlistMember,
  type WatchlistDetailOut,
  type WatchlistOut,
} from "@/lib/api";
import { fmtScore } from "@/lib/format";
import { Panel } from "@/components/ui/Panel";
import { DataTable } from "@/components/ui/Table";
import { ConfidenceBadge, ThesisBadge } from "@/components/ui/Badges";
import { EmptyState, LoadingState } from "@/components/ui/States";
import { Button } from "@/components/ui/Button";
import { Label, Select, TextInput } from "@/components/ui/Input";

/**
 * Watchlist page.
 *
 * Lists saved watchlists in a picker, shows the active watchlist's members
 * with their latest composite score / confidence / thesis, and exposes
 * inline add/remove controls. A "create new list" form surfaces when the
 * operator has no watchlists yet.
 */
export function WatchlistPage() {
  const watchlists = useQuery({
    queryKey: ["watchlists"],
    queryFn: listWatchlists,
  });

  const [activeId, setActiveId] = useState<number | null>(null);
  const resolvedActive = activeId ?? (watchlists.data?.[0]?.id ?? null);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-xl font-semibold tracking-tight">Watchlists</h1>
          <p className="text-2xs text-foreground-muted">
            Track securities of interest. Click a ticker to open the company
            page; use the form below to add positions.
          </p>
        </div>
        {watchlists.data && watchlists.data.length > 0 && (
          <WatchlistPicker
            watchlists={watchlists.data}
            value={resolvedActive}
            onChange={setActiveId}
          />
        )}
      </header>

      {watchlists.isLoading && <LoadingState />}

      {watchlists.data && watchlists.data.length === 0 && (
        <CreateWatchlistPanel
          onCreated={(w) => setActiveId(w.id)}
        />
      )}

      {resolvedActive !== null && (
        <WatchlistDetail
          watchlistId={resolvedActive}
          onDeleted={() => setActiveId(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Picker + create button
// ---------------------------------------------------------------------------

function WatchlistPicker({
  watchlists,
  value,
  onChange,
}: {
  watchlists: WatchlistOut[];
  value: number | null;
  onChange: (id: number) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <Label htmlFor="watchlist-picker">Watchlist</Label>
      <Select
        id="watchlist-picker"
        value={value ?? ""}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-52"
      >
        {watchlists.map((w) => (
          <option key={w.id} value={w.id}>
            {w.name}
          </option>
        ))}
      </Select>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create-watchlist panel (empty state)
// ---------------------------------------------------------------------------

function CreateWatchlistPanel({
  onCreated,
}: {
  onCreated: (w: WatchlistDetailOut) => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const create = useMutation({
    mutationFn: () =>
      createWatchlist({ name: name.trim(), description: description.trim() || null }),
    onSuccess: (w) => {
      qc.invalidateQueries({ queryKey: ["watchlists"] });
      onCreated(w);
      setName("");
      setDescription("");
    },
  });

  return (
    <Panel
      title="Create your first watchlist"
      description="Name it; you can add securities on the next screen."
    >
      <form
        className="grid gap-3 sm:grid-cols-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim()) return;
          create.mutate();
        }}
      >
        <div className="space-y-1">
          <Label htmlFor="new-wl-name">Name</Label>
          <TextInput
            id="new-wl-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="High-conviction ideas"
            required
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="new-wl-description">Description</Label>
          <TextInput
            id="new-wl-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional"
          />
        </div>
        <div className="flex items-end">
          <Button
            variant="primary"
            type="submit"
            disabled={!name.trim() || create.isPending}
            loading={create.isPending}
          >
            Create watchlist
          </Button>
        </div>
        {create.isError && (
          <div className="sm:col-span-3 text-xs text-danger">
            {(create.error as Error).message}
          </div>
        )}
      </form>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Watchlist detail — members table + add / delete controls
// ---------------------------------------------------------------------------

function WatchlistDetail({
  watchlistId,
  onDeleted,
}: {
  watchlistId: number;
  onDeleted: () => void;
}) {
  const qc = useQueryClient();

  const detail = useQuery({
    queryKey: ["watchlist", watchlistId],
    queryFn: () => getWatchlist(watchlistId),
  });

  const removeList = useMutation({
    mutationFn: () => deleteWatchlist(watchlistId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["watchlists"] });
      onDeleted();
    },
  });

  if (detail.isLoading) return <LoadingState />;
  if (!detail.data) {
    return <EmptyState title="No data" description="Couldn't load watchlist." />;
  }

  const wl = detail.data;

  return (
    <>
      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <h2 className="text-base font-semibold">{wl.name}</h2>
          {wl.description && (
            <p className="text-xs text-foreground-muted">{wl.description}</p>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          disabled={removeList.isPending}
          onClick={() => {
            if (confirm(`Delete watchlist "${wl.name}"?`)) removeList.mutate();
          }}
        >
          Delete list
        </Button>
      </div>

      <Panel
        title="Members"
        description={`${wl.members.length} securit${wl.members.length === 1 ? "y" : "ies"}`}
        bodyClassName="px-0 py-0"
      >
        <MembersTable watchlistId={watchlistId} members={wl.members} />
      </Panel>

      <AddMemberPanel watchlistId={watchlistId} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Members table
// ---------------------------------------------------------------------------

type MemberRow = WatchlistDetailOut["members"][number];

function MembersTable({
  watchlistId,
  members,
}: {
  watchlistId: number;
  members: MemberRow[];
}) {
  const qc = useQueryClient();
  const remove = useMutation({
    mutationFn: (securityId: number) =>
      removeWatchlistMember(watchlistId, securityId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["watchlist", watchlistId] });
    },
  });

  if (members.length === 0) {
    return (
      <EmptyState
        title="No members yet"
        description="Use the form below to add securities to this list."
      />
    );
  }

  return (
    <DataTable
      dense
      rows={[...members].sort((a, b) =>
        (b.composite ?? -Infinity) - (a.composite ?? -Infinity),
      )}
      rowKey={(r) => r.security_id}
      empty="No members."
      columns={[
        {
          key: "ticker",
          header: "Ticker",
          cell: (r) =>
            r.ticker ? (
              <Link
                to={`/company/${r.security_id}`}
                className="font-mono font-medium text-foreground-primary hover:text-accent-strong"
              >
                {r.ticker}
              </Link>
            ) : (
              `#${r.security_id}`
            ),
        },
        {
          key: "name",
          header: "Company",
          cell: (r) => (
            <span className="max-w-48 truncate text-xs text-foreground-secondary">
              {r.company_name ?? "—"}
            </span>
          ),
        },
        {
          key: "composite",
          header: "Composite",
          align: "right",
          cell: (r) => fmtScore(r.composite),
        },
        {
          key: "confidence",
          header: "Confidence",
          cell: (r) =>
            r.confidence_band ? (
              <ConfidenceBadge band={r.confidence_band as never} />
            ) : (
              "—"
            ),
        },
        {
          key: "thesis",
          header: "Thesis",
          cell: (r) =>
            r.thesis_status ? (
              <ThesisBadge status={r.thesis_status as never} />
            ) : (
              "—"
            ),
        },
        {
          key: "notes",
          header: "Notes",
          cell: (r) => (
            <span
              className="max-w-40 truncate text-2xs text-foreground-muted"
              title={r.notes ?? undefined}
            >
              {r.notes ?? "—"}
            </span>
          ),
        },
        {
          key: "actions",
          header: "",
          align: "right",
          cell: (r) => (
            <Button
              variant="ghost"
              size="sm"
              disabled={remove.isPending}
              onClick={() => remove.mutate(r.security_id)}
            >
              Remove
            </Button>
          ),
        },
      ]}
    />
  );
}

// ---------------------------------------------------------------------------
// Add-member form
// ---------------------------------------------------------------------------

function AddMemberPanel({ watchlistId }: { watchlistId: number }) {
  const qc = useQueryClient();
  const universe = useQuery({
    queryKey: ["watchlist-universe-picker"],
    queryFn: () => listSecurities({ limit: 1500 }),
  });

  const [securityId, setSecurityId] = useState<number | null>(null);
  const [notes, setNotes] = useState("");

  const add = useMutation({
    mutationFn: () =>
      addWatchlistMember(watchlistId, {
        security_id: securityId!,
        notes: notes.trim() || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["watchlist", watchlistId] });
      setSecurityId(null);
      setNotes("");
    },
  });

  const canSubmit = securityId !== null;

  return (
    <Panel
      title="Add security"
      description="Re-submit an existing member to update its notes."
    >
      <form
        className="grid gap-3 sm:grid-cols-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) add.mutate();
        }}
      >
        <div className="space-y-1 sm:col-span-2">
          <Label htmlFor="wl-security">Security</Label>
          <Select
            id="wl-security"
            value={securityId ?? ""}
            onChange={(e) =>
              setSecurityId(e.target.value ? Number(e.target.value) : null)
            }
            disabled={universe.isLoading}
            required
          >
            <option value="">Select…</option>
            {universe.data?.map((s) => (
              <option key={s.id} value={s.id}>
                {s.ticker} — {s.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="space-y-1 sm:col-span-1">
          <Label htmlFor="wl-notes">Notes</Label>
          <TextInput
            id="wl-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional"
          />
        </div>
        <div className="flex items-end sm:col-span-1">
          <Button
            variant="primary"
            type="submit"
            disabled={!canSubmit || add.isPending}
            loading={add.isPending}
            className="w-full"
          >
            Add
          </Button>
        </div>
        {add.isError && (
          <div className="sm:col-span-4 text-xs text-danger">
            {(add.error as Error).message}
          </div>
        )}
      </form>
    </Panel>
  );
}
